import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Group, CreateGroupInput, UpdateGroupInput } from "@/types/groups";

export const useGroups = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: currentUserId } = useQuery({
    queryKey: ["currentUserId"],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      return session?.session?.user?.id || null;
    },
  });

  const { data: groups, isLoading, error } = useQuery({
    queryKey: ["groups"],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) {
        throw new Error("Not authenticated");
      }

      const { data, error } = await supabase
        .from("groups")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Group[];
    },
  });

  const createGroup = useMutation({
    mutationFn: async (input: CreateGroupInput) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) {
        throw new Error("Not authenticated");
      }

      const { data, error } = await supabase
        .from("groups")
        .insert({
          group_leader_id: session.session.user.id,
          name: input.name || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data as Group;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      toast({
        title: "Grupp skapad",
        description: "Din nya grupp har skapats. Ange första möjliga datum för att fortsätta.",
      });
      return data;
    },
    onError: (error) => {
      console.error("Error creating group:", error);
      toast({
        title: "Fel",
        description: "Kunde inte skapa grupp. Försök igen.",
        variant: "destructive",
      });
    },
  });

  const updateGroup = useMutation({
    mutationFn: async ({ id, ...input }: UpdateGroupInput & { id: string }) => {
      const { data, error } = await supabase
        .from("groups")
        .update(input)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Group;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
    onError: (error) => {
      console.error("Error updating group:", error);
      toast({
        title: "Fel",
        description: "Kunde inte uppdatera grupp. Försök igen.",
        variant: "destructive",
      });
    },
  });

  const acceptSchedule = useMutation({
    mutationFn: async (groupId: string) => {
      const { data, error } = await supabase.rpc("accept_group_schedule", {
        group_id_param: groupId,
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string; assigned_group?: string; already_scheduled?: boolean };
      
      if (!result.success) {
        throw new Error(result.error || "Failed to accept schedule");
      }
      
      return result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      
      const groupType = result.assigned_group === "intervention" 
        ? "Interventionsgrupp" 
        : "Kontrollgrupp";
      
      toast({
        title: "Schema accepterat",
        description: `Din grupp har randomiserats till: ${groupType}`,
      });
    },
    onError: (error) => {
      console.error("Error accepting schedule:", error);
      toast({
        title: "Fel",
        description: error instanceof Error ? error.message : "Kunde inte acceptera schema. Försök igen.",
        variant: "destructive",
      });
    },
  });

  const deleteGroup = useMutation({
    mutationFn: async (groupId: string) => {
      const { error } = await supabase
        .from("groups")
        .delete()
        .eq("id", groupId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      toast({
        title: "Grupp borttagen",
        description: "Gruppen har tagits bort.",
      });
    },
    onError: (error) => {
      console.error("Error deleting group:", error);
      toast({
        title: "Fel",
        description: "Kunde inte ta bort grupp. Försök igen.",
        variant: "destructive",
      });
    },
  });

  return {
    groups,
    isLoading,
    error,
    currentUserId: currentUserId || null,
    createGroup,
    updateGroup,
    acceptSchedule,
    deleteGroup,
  };
};
